import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { authHeaders } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FormatsPage() {
  const [formats, setFormats] = useState([]);
  const [selected, setSelected] = useState(null);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    (async () => {
      const res = await fetch(`${API}/api/formats/list`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setFormats(data);
        if (data.length > 0) setSelected(data[0]);
      }
    })();
  }, []);

  const handleSendAll = async () => {
    if (!window.confirm('Se enviaran los 7 formatos a info@iqeslowvoltage.com. Continuar?')) return;
    setSending(true);
    setMsg('');
    try {
      const res = await fetch(`${API}/api/formats/send-all-to-admin`, { method: 'POST', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setMsg(`OK - ${data.sent_count} formatos enviados a ${data.sent_to}`);
      } else {
        setMsg(`Error al enviar (${res.status}). Usa el boton "Descargar ZIP" como alternativa.`);
      }
    } catch (e) {
      setMsg('Error de red al enviar. Usa "Descargar ZIP" como alternativa.');
    }
    setSending(false);
    setTimeout(() => setMsg(''), 8000);
  };

  const handleDownloadZip = async (variant = 'html') => {
    setDownloading(true);
    setMsg('');
    try {
      const endpoint = variant === 'docx' ? 'download-all-docx' : 'download-all';
      const res = await fetch(`${API}/api/formats/${endpoint}`, { credentials: 'include', headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = variant === 'docx'
        ? `IQES-Formatos-Word-${new Date().toISOString().slice(0, 10)}.zip`
        : `IQES-Formatos-HTML-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setMsg(`OK - ZIP ${variant.toUpperCase()} descargado. ${variant === 'docx' ? 'Abre cada .docx en Word/LibreOffice/Google Docs y edita lo que necesites.' : 'Abre cada HTML y usa "Imprimir -> Guardar como PDF".'}`);
    } catch (e) {
      setMsg(`Error al descargar: ${e.message}`);
    }
    setDownloading(false);
    setTimeout(() => setMsg(''), 9000);
  };

  const handleDownloadOne = async (key, name, variant = 'html') => {
    try {
      const endpoint = variant === 'docx' ? `download-docx/${key}` : `download/${key}`;
      const res = await fetch(`${API}/api/formats/${endpoint}`, { credentials: 'include', headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `IQES-${key}.${variant === 'docx' ? 'docx' : 'html'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setMsg(`Error descargando ${name}: ${e.message}`);
      setTimeout(() => setMsg(''), 5000);
    }
  };

  return (
    <div data-testid="formats-page" className="space-y-6">
      <div className="flex justify-between items-start border-b border-slate-700 pb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Formatos del Sistema</h1>
          <p className="text-slate-300 text-sm mt-1">Preview de todos los documentos: WO, Cotizaciones, Facturas, SOW, BOM, Perfil de Empresa, Emails</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="download-zip-docx-btn" onClick={() => handleDownloadZip('docx')} disabled={downloading} className="bg-indigo-600 hover:bg-indigo-700">
            {downloading ? 'Descargando...' : 'Descargar Word ZIP (los 7)'}
          </Button>
          <Button data-testid="download-zip-btn" onClick={() => handleDownloadZip('html')} disabled={downloading} className="bg-green-600 hover:bg-green-700">
            {downloading ? 'Descargando...' : 'Descargar HTML ZIP (los 7)'}
          </Button>
          <Button data-testid="send-all-btn" onClick={handleSendAll} disabled={sending} className="bg-blue-600 hover:bg-blue-700">
            {sending ? 'Enviando...' : 'Enviar a info@iqeslowvoltage.com'}
          </Button>
        </div>
      </div>

      {msg && (
        <div data-testid="formats-msg" className={`${msg.startsWith('OK') ? 'bg-green-900/30 border-green-700 text-green-200' : 'bg-red-900/30 border-red-700 text-red-200'} border px-4 py-2 rounded text-sm`}>
          {msg}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="space-y-2">
          {formats.map(f => (
            <div
              key={f.key}
              className={`rounded-lg border transition ${selected?.key === f.key ? 'bg-blue-900/40 border-blue-500' : 'bg-slate-800/60 border-slate-700 hover:bg-slate-700/60'}`}
            >
              <button
                data-testid={`format-${f.key}`}
                onClick={() => setSelected(f)}
                className="w-full text-left p-3"
              >
                <div className={`font-medium text-sm ${selected?.key === f.key ? 'text-white' : 'text-slate-200'}`}>{f.name}</div>
                <div className="text-xs text-slate-500 mt-1">{f.key}</div>
              </button>
              <div className="flex gap-1 px-3 pb-2">
                <button
                  data-testid={`download-html-${f.key}`}
                  onClick={() => handleDownloadOne(f.key, f.name, 'html')}
                  className="text-xs text-cyan-400 hover:text-cyan-300 flex-1 text-left"
                  title="Descargar como HTML"
                >
                  HTML
                </button>
                <button
                  data-testid={`download-docx-${f.key}`}
                  onClick={() => handleDownloadOne(f.key, f.name, 'docx')}
                  className="text-xs text-indigo-400 hover:text-indigo-300 flex-1 text-left"
                  title="Descargar como Word editable"
                >
                  Word
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-3">
          {selected && (
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="flex flex-row justify-between items-center">
                <CardTitle className="text-white text-sm">{selected.name}</CardTitle>
                <a
                  data-testid="format-new-tab"
                  href={`${API}${selected.preview_url}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-blue-400 hover:text-blue-300 underline"
                >
                  Abrir en nueva pestana
                </a>
              </CardHeader>
              <CardContent>
                <iframe
                  key={selected.key}
                  data-testid="format-iframe"
                  src={`${API}${selected.preview_url}`}
                  title={selected.name}
                  className="w-full h-[700px] bg-white rounded border border-slate-600"
                />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
